import { useState, useEffect } from 'react';
import './App.css';

// ─── 숫자 입력 컴포넌트 ───────────────────────────────────
function NumberInput({ id, value, onChange, min, max, unit }) {
  const adjust = (delta) => {
    const next = value + delta;
    if (next >= min && next <= max) onChange(next);
  };
  return (
    <div className="number-input-wrap">
      <button className="num-btn" onClick={() => adjust(-1)}>−</button>
      <input
        className="num-input"
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={e => {
          const v = parseInt(e.target.value);
          if (!isNaN(v) && v >= min && v <= max) onChange(v);
        }}
      />
      <span className="num-unit">{unit}</span>
      <button className="num-btn" onClick={() => adjust(1)}>+</button>
    </div>
  );
}

// ─── 성별 선택 컴포넌트 ──────────────────────────────────
function GenderSelect({ value, onChange }) {
  return (
    <div className="gender-row">
      <button className={`gender-btn${value === 'male' ? ' selected' : ''}`} onClick={() => onChange('male')}>👨 남성</button>
      <button className={`gender-btn${value === 'female' ? ' selected' : ''}`} onClick={() => onChange('female')}>👩 여성</button>
    </div>
  );
}

// ─── 결과 카드 컴포넌트 ──────────────────────────────────
function ResultCard({ result }) {
  if (!result) return null;
  return (
    <div className={`result-card show res-${result.color}`}>
      <div className="result-number">{result.number}</div>
      <div className="result-label">{result.label}</div>
      <div className="result-desc">{result.desc}</div>
      <div className="result-tip">{result.tip}</div>
    </div>
  );
}

// ─── 광고 슬롯 ───────────────────────────────────────────
function AdSlot() {
  useEffect(() => {
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {}
  }, []);
  return (
    <div className="ad-slot">
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client="ca-pub-1617340854575319"
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}

// ─── 유튜브 배너 ─────────────────────────────────────────
function YoutubeBanner() {
  return (
    <a
      className="yt-banner"
      href="https://www.youtube.com/@healthy120"
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="yt-icon">▶</span>
      <span className="yt-text">
        <strong>건강120 유튜브 쇼츠</strong>
        <small>매일 업데이트되는 어르신 건강 영상 보러가기</small>
      </span>
      <span className="yt-arrow">›</span>
    </a>
  );
}

// ─── BMI 탭 ──────────────────────────────────────────────
function BMITab() {
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState(55);
  const [height, setHeight] = useState(165);
  const [weight, setWeight] = useState(65);
  const [result, setResult] = useState(null);

  const calc = () => {
    const h = height / 100;
    const bmi = (weight / (h * h)).toFixed(1);
    const idealMin = (18.5 * h * h).toFixed(1);
    const idealMax = (22.9 * h * h).toFixed(1);

    let color, label, desc, tip;
    if (bmi < 18.5) {
      color = 'blue'; label = '저체중';
      desc = `BMI ${bmi} — 정상 체중보다 가볍습니다.\n적정 체중은 ${idealMin}kg ~ ${idealMax}kg 사이예요.`;
      tip = '💡 단백질 위주의 식단으로 근육량을 늘리는 것이 도움이 됩니다.';
    } else if (bmi < 23) {
      color = 'green'; label = '✅ 정상 체중';
      desc = `BMI ${bmi} — 아주 건강한 체중입니다!\n적정 체중 범위: ${idealMin}kg ~ ${idealMax}kg`;
      tip = '💡 현재 생활 습관을 유지하세요. 규칙적인 운동을 꾸준히 하세요.';
    } else if (bmi < 25) {
      color = 'orange'; label = '과체중 (주의)';
      desc = `BMI ${bmi} — 정상 범위를 조금 넘었어요.\n목표 체중: ${idealMax}kg 이하로 줄이면 좋아요.`;
      tip = '💡 식사량을 조금 줄이고, 하루 30분 걷기부터 시작해보세요.';
    } else {
      color = 'red'; label = '비만';
      desc = `BMI ${bmi} — 건강 관리가 필요한 상태입니다.\n목표 체중: ${idealMax}kg 이하를 목표로 하세요.`;
      tip = '💡 전문의와 상담하여 체계적인 체중 감량 계획을 세우시길 권장합니다.';
    }
    setResult({ number: bmi, label, desc, tip, color });
  };

  return (
    <div className="content">
      <div className="card">
        <div className="card-title"><span className="card-icon">⚖️</span> 체중 · BMI 계산기</div>
        <div className="input-group">
          <label className="input-label">성별</label>
          <GenderSelect value={gender} onChange={setGender} />
        </div>
        <div className="input-group">
          <label className="input-label">나이</label>
          <NumberInput value={age} onChange={setAge} min={10} max={100} unit="세" />
        </div>
        <div className="input-group">
          <label className="input-label">키</label>
          <NumberInput value={height} onChange={setHeight} min={100} max={220} unit="cm" />
        </div>
        <div className="input-group">
          <label className="input-label">몸무게</label>
          <NumberInput value={weight} onChange={setWeight} min={20} max={200} unit="kg" />
        </div>
        <button className="calc-btn" onClick={calc}>📊 지금 바로 확인하기</button>
        <ResultCard result={result} />
        {result && <AdSlot />}
      </div>
      <div className="card">
        <div className="card-title">📋 BMI 기준표 (아시아인 기준)</div>
        <table className="range-table">
          <thead><tr><th>BMI 수치</th><th>판정</th></tr></thead>
          <tbody>
            <tr><td><span className="dot" style={{background:'#1A6B9A'}}></span>18.5 미만</td><td>저체중</td></tr>
            <tr><td><span className="dot" style={{background:'#2E7D52'}}></span>18.5 ~ 22.9</td><td>✅ 정상</td></tr>
            <tr><td><span className="dot" style={{background:'#E67E22'}}></span>23.0 ~ 24.9</td><td>과체중</td></tr>
            <tr><td><span className="dot" style={{background:'#C0392B'}}></span>25.0 이상</td><td>비만</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── BMR 탭 ──────────────────────────────────────────────
const ACTIVITIES = [
  { id: 1, mult: 1.2,   icon: '🛋️', label: '거의 안 움직여요',  sub: '하루 대부분 앉아서 생활' },
  { id: 2, mult: 1.375, icon: '🚶', label: '가볍게 움직여요',   sub: '일주일 1~3회 가벼운 운동' },
  { id: 3, mult: 1.55,  icon: '🏃', label: '보통으로 움직여요', sub: '일주일 3~5회 운동' },
  { id: 4, mult: 1.725, icon: '💪', label: '많이 움직여요',     sub: '매일 활발하게 운동' },
];

function BMRTab() {
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState(55);
  const [height, setHeight] = useState(165);
  const [weight, setWeight] = useState(65);
  const [actId, setActId] = useState(2);
  const [result, setResult] = useState(null);

  const calc = () => {
    let bmr = gender === 'male'
      ? 10 * weight + 6.25 * height - 5 * age + 5
      : 10 * weight + 6.25 * height - 5 * age - 161;
    const act = ACTIVITIES.find(a => a.id === actId);
    const tdee = Math.round(bmr * act.mult);
    const bmrR = Math.round(bmr);
    setResult({
      number: `${tdee.toLocaleString()} kcal`,
      label: '하루에 이만큼 드셔야 해요',
      desc: `기초대사량(아무것도 안 해도 소모): ${bmrR.toLocaleString()} kcal\n활동 수준(${act.label}) 반영: ${tdee.toLocaleString()} kcal\n\n• 체중 줄이려면: 하루 ${(tdee-300).toLocaleString()} kcal 목표\n• 체중 늘리려면: 하루 ${(tdee+300).toLocaleString()} kcal 목표`,
      tip: '💡 하루 300kcal 줄이면 한 달에 약 1~1.5kg 감량 효과가 있어요.',
      color: 'green',
    });
  };

  return (
    <div className="content">
      <div className="card">
        <div className="card-title"><span className="card-icon">🔥</span> 하루 필요 칼로리 계산기</div>
        <div className="input-group">
          <label className="input-label">성별</label>
          <GenderSelect value={gender} onChange={setGender} />
        </div>
        <div className="input-group">
          <label className="input-label">나이</label>
          <NumberInput value={age} onChange={setAge} min={10} max={100} unit="세" />
        </div>
        <div className="input-group">
          <label className="input-label">키</label>
          <NumberInput value={height} onChange={setHeight} min={100} max={220} unit="cm" />
        </div>
        <div className="input-group">
          <label className="input-label">몸무게</label>
          <NumberInput value={weight} onChange={setWeight} min={20} max={200} unit="kg" />
        </div>
        <div className="input-group">
          <label className="input-label">평소 활동량</label>
          <div className="activity-list">
            {ACTIVITIES.map(a => (
              <button key={a.id} className={`activity-btn${actId === a.id ? ' selected' : ''}`} onClick={() => setActId(a.id)}>
                {a.icon} {a.label} <small>{a.sub}</small>
              </button>
            ))}
          </div>
        </div>
        <button className="calc-btn" onClick={calc}>🔥 하루 칼로리 확인하기</button>
        <ResultCard result={result} />
        {result && <AdSlot />}
      </div>
      <div className="card">
        <div className="card-title">🍽️ 식품별 칼로리 참고</div>
        <table className="range-table">
          <thead><tr><th>식품</th><th>칼로리</th></tr></thead>
          <tbody>
            <tr><td>밥 1공기 (200g)</td><td>약 300kcal</td></tr>
            <tr><td>달걀 1개</td><td>약 80kcal</td></tr>
            <tr><td>바나나 1개</td><td>약 90kcal</td></tr>
            <tr><td>두부 반모</td><td>약 100kcal</td></tr>
            <tr><td>삼겹살 100g</td><td>약 330kcal</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── 혈압 탭 ─────────────────────────────────────────────
function BPTab() {
  const [sys, setSys] = useState(120);
  const [dia, setDia] = useState(80);
  const [result, setResult] = useState(null);

  const calc = () => {
    let color, label, desc, tip;
    if (sys < 120 && dia < 80) {
      color = 'green'; label = '✅ 정상 혈압';
      desc = `수축기 ${sys} / 이완기 ${dia} mmHg\n아주 건강한 혈압 수치입니다!`;
      tip = '💡 현재 생활 습관을 잘 유지하세요. 싱겁게 먹고 꾸준히 운동하면 계속 유지할 수 있어요.';
    } else if (sys < 130 && dia < 80) {
      color = 'green'; label = '높은 정상';
      desc = `수축기 ${sys} / 이완기 ${dia} mmHg\n정상이지만 조금 높아지고 있어요.`;
      tip = '💡 짠 음식을 줄이고, 하루 30분 이상 걷기를 시작해보세요.';
    } else if (sys < 140 && dia < 90) {
      color = 'orange'; label = '⚠️ 고혈압 전단계';
      desc = `수축기 ${sys} / 이완기 ${dia} mmHg\n주의가 필요한 수치입니다.`;
      tip = '💡 소금 섭취를 줄이고, 금연·절주가 필요해요. 정기적으로 혈압을 측정하세요.';
    } else if (sys < 160 && dia < 100) {
      color = 'red'; label = '🚨 고혈압 1단계';
      desc = `수축기 ${sys} / 이완기 ${dia} mmHg\n고혈압 상태입니다. 병원 방문을 권장합니다.`;
      tip = '💡 가능한 빨리 의사와 상담하세요. 약물 치료가 필요할 수 있습니다.';
    } else {
      color = 'red'; label = '🚨 고혈압 2단계 (위험)';
      desc = `수축기 ${sys} / 이완기 ${dia} mmHg\n매우 높은 혈압입니다. 즉시 병원을 방문하세요.`;
      tip = '⚠️ 뇌졸중, 심근경색 위험이 높습니다. 다시 측정 후 즉시 의료기관을 방문하세요.';
    }
    setResult({ number: `${sys}/${dia}`, label, desc, tip, color });
  };

  return (
    <div className="content">
      <div className="card">
        <div className="card-title"><span className="card-icon">❤️</span> 혈압 단계 확인기</div>
        <p className="input-hint">혈압계로 측정한 숫자를 입력하세요.<br />위 숫자(수축기)와 아래 숫자(이완기)를 입력하면 됩니다.</p>
        <div className="input-group" style={{marginTop: '16px'}}>
          <label className="input-label">📍 위 숫자 (수축기 혈압)</label>
          <NumberInput value={sys} onChange={setSys} min={60} max={220} unit="mmHg" />
        </div>
        <div className="input-group">
          <label className="input-label">📍 아래 숫자 (이완기 혈압)</label>
          <NumberInput value={dia} onChange={setDia} min={40} max={140} unit="mmHg" />
        </div>
        <button className="calc-btn" onClick={calc}>❤️ 혈압 단계 확인하기</button>
        <ResultCard result={result} />
        {result && <AdSlot />}
      </div>
      <div className="card">
        <div className="card-title">📋 혈압 기준표</div>
        <table className="range-table">
          <thead><tr><th>단계</th><th>수축기</th><th>이완기</th></tr></thead>
          <tbody>
            <tr><td><span className="dot" style={{background:'#2E7D52'}}></span>정상</td><td>120 미만</td><td>80 미만</td></tr>
            <tr><td><span className="dot" style={{background:'#4CAF7D'}}></span>높은 정상</td><td>120~129</td><td>80 미만</td></tr>
            <tr><td><span className="dot" style={{background:'#E67E22'}}></span>고혈압 전단계</td><td>130~139</td><td>80~89</td></tr>
            <tr><td><span className="dot" style={{background:'#E74C3C'}}></span>고혈압 1단계</td><td>140~159</td><td>90~99</td></tr>
            <tr><td><span className="dot" style={{background:'#C0392B'}}></span>고혈압 2단계</td><td>160 이상</td><td>100 이상</td></tr>
          </tbody>
        </table>
        <p className="table-note">※ 이 결과는 참고용이며, 정확한 진단은 의사에게 받으세요.</p>
      </div>
    </div>
  );
}

// ─── 메인 앱 ─────────────────────────────────────────────
const TABS = [
  { id: 'bmi', label: '⚖️ 체중' },
  { id: 'bmr', label: '🔥 칼로리' },
  { id: 'bp',  label: '❤️ 혈압' },
];

export default function App() {
  const [activeTab, setActiveTab] = useState('bmi');

  return (
    <div className="app">
      <header className="header">
        <h1>🌿 건강120</h1>
        <p>내 몸 상태를 쉽게 확인해보세요</p>
      </header>

      <YoutubeBanner />

      <nav className="tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`tab-btn${activeTab === t.id ? ' active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main className="main">
        {activeTab === 'bmi' && <BMITab />}
        {activeTab === 'bmr' && <BMRTab />}
        {activeTab === 'bp'  && <BPTab />}
      </main>

      <footer className="footer">
        건강120 · bmi.20jobs.co.kr<br />
        본 서비스는 참고용이며 의학적 진단을 대체하지 않습니다.
      </footer>
    </div>
  );
}
